import { useCallback, useEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';
import { Modal } from '@mantine/core';
import { IconChevronLeft, IconChevronRight, IconMinus, IconPlus, IconX, IconZoomReset } from '@tabler/icons-react';

export interface LightboxImage {
  src: string;
  alt: string;
  caption?: string | null;
}

const MIN = 1;
const MAX = 5;

/**
 * Full-screen photo viewer: pinch or double-tap to zoom, drag to look around, swipe (or the arrow
 * buttons) between photos. Buttons for zoom in, out and reset make it usable one-handed and with a
 * screen reader. No library: pointer events plus a CSS transform; `touch-action: none` on the stage
 * keeps the browser from zooming the whole page instead.
 */
export function ImageLightbox({
  images,
  index,
  onIndex,
  onClose,
}: {
  images: LightboxImage[];
  index: number | null;
  onIndex: (i: number) => void;
  onClose: () => void;
}) {
  const open = index !== null && index >= 0 && index < images.length;
  const img = open ? images[index as number] : null;
  const [scale, setScale] = useState(1);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const pointers = useRef(new Map<number, { x: number; y: number }>());
  const pinch = useRef<{ dist: number; scale: number } | null>(null);
  const drag = useRef<{ x: number; y: number; px: number; py: number; moved: boolean } | null>(null);
  const lastTap = useRef(0);

  const reset = useCallback(() => {
    setScale(1);
    setPos({ x: 0, y: 0 });
  }, []);

  useEffect(() => {
    reset();
  }, [index, reset]);

  const zoomTo = (next: number) => {
    const s = Math.min(MAX, Math.max(MIN, next));
    setScale(s);
    if (s === 1) setPos({ x: 0, y: 0 });
  };

  const go = (delta: number) => {
    if (!open || images.length < 2) return;
    onIndex(((index as number) + delta + images.length) % images.length);
  };

  const onPointerDown = (e: ReactPointerEvent<HTMLDivElement>) => {
    e.currentTarget.setPointerCapture(e.pointerId);
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pointers.current.size === 2) {
      const [a, b] = Array.from(pointers.current.values());
      pinch.current = { dist: Math.hypot(a.x - b.x, a.y - b.y), scale };
      drag.current = null;
    } else if (pointers.current.size === 1) {
      drag.current = { x: e.clientX, y: e.clientY, px: pos.x, py: pos.y, moved: false };
    }
  };

  const onPointerMove = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (!pointers.current.has(e.pointerId)) return;
    pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
    if (pinch.current && pointers.current.size >= 2) {
      const [a, b] = Array.from(pointers.current.values());
      const d = Math.hypot(a.x - b.x, a.y - b.y);
      if (pinch.current.dist > 0) zoomTo(pinch.current.scale * (d / pinch.current.dist));
      return;
    }
    const dr = drag.current;
    if (!dr) return;
    const dx = e.clientX - dr.x;
    const dy = e.clientY - dr.y;
    if (Math.abs(dx) > 6 || Math.abs(dy) > 6) dr.moved = true;
    if (scale > 1) setPos({ x: dr.px + dx, y: dr.py + dy });
  };

  const onPointerUp = (e: ReactPointerEvent<HTMLDivElement>) => {
    const dr = drag.current;
    pointers.current.delete(e.pointerId);
    if (pointers.current.size < 2) pinch.current = null;
    if (pointers.current.size > 0) return;
    drag.current = null;
    if (!dr) return;
    const dx = e.clientX - dr.x;
    if (scale === 1 && dr.moved && Math.abs(dx) > 60) {
      go(dx < 0 ? 1 : -1);
      return;
    }
    if (!dr.moved) {
      const now = Date.now();
      if (now - lastTap.current < 300) {
        zoomTo(scale > 1 ? 1 : 2.5);
        lastTap.current = 0;
      } else {
        lastTap.current = now;
      }
    }
  };

  const onWheel = (e: React.WheelEvent<HTMLDivElement>) => {
    zoomTo(scale * (e.deltaY < 0 ? 1.15 : 1 / 1.15));
  };

  return (
    <Modal
      opened={open}
      onClose={onClose}
      fullScreen
      withCloseButton={false}
      padding={0}
      radius={0}
      transitionProps={{ transition: 'fade', duration: 120 }}
      classNames={{ content: 'proc-lightbox-content', body: 'proc-lightbox-body' }}
      title={img?.alt || 'Photo'}
      styles={{ header: { display: 'none' } }}
      onKeyDown={(e) => {
        if (e.key === 'ArrowRight') go(1);
        if (e.key === 'ArrowLeft') go(-1);
        if (e.key === '+' || e.key === '=') zoomTo(scale * 1.4);
        if (e.key === '-') zoomTo(scale / 1.4);
      }}
    >
      {img && (
        <div className="proc-lightbox">
          <div className="bar top">
            <span className="count" aria-live="polite">
              {images.length > 1 ? `Photo ${(index as number) + 1} of ${images.length}` : 'Photo'}
            </span>
            <button type="button" className="lbbtn" onClick={onClose} aria-label="Close photo" data-autofocus>
              <IconX size={24} aria-hidden="true" />
            </button>
          </div>
          <div
            className="stage"
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerCancel={onPointerUp}
            onWheel={onWheel}
          >
            <img
              src={img.src}
              alt={img.alt}
              draggable={false}
              style={{ transform: `translate(${pos.x}px, ${pos.y}px) scale(${scale})` }}
            />
          </div>
          {img.caption && <p className="caption">{img.caption}</p>}
          <div className="bar bottom">
            {images.length > 1 && (
              <button type="button" className="lbbtn" onClick={() => go(-1)} aria-label="Previous photo">
                <IconChevronLeft size={26} aria-hidden="true" />
              </button>
            )}
            <button type="button" className="lbbtn" onClick={() => zoomTo(scale / 1.5)} aria-label="Zoom out" disabled={scale <= MIN}>
              <IconMinus size={24} aria-hidden="true" />
            </button>
            <button type="button" className="lbbtn" onClick={reset} aria-label="Fit to screen" disabled={scale === 1}>
              <IconZoomReset size={24} aria-hidden="true" />
            </button>
            <button type="button" className="lbbtn" onClick={() => zoomTo(scale * 1.5)} aria-label="Zoom in" disabled={scale >= MAX}>
              <IconPlus size={24} aria-hidden="true" />
            </button>
            {images.length > 1 && (
              <button type="button" className="lbbtn" onClick={() => go(1)} aria-label="Next photo">
                <IconChevronRight size={26} aria-hidden="true" />
              </button>
            )}
          </div>
        </div>
      )}
    </Modal>
  );
}
